// Itens obrigatórios por grupo (Rafael 21/10, planilha de atributos 15-10): ART obrigatória quando o grupo
// EQUIPAMENTOS tem Gerador; RRT quando tem qualquer equipamento. A regra vem do catálogo, no GRUPO:
// orcamento_config.itens_obrigatorios = [{ produto, nome, quando: 'itens', itens: [ids] }
//                                        | { produto, nome, quando: 'qualquer_item', exceto: [ids] }].
//
// Ao escolher o item que dispara, a tela cria a linha obrigatória (mesmo fornecedor). Se alguém apagar, o
// Salvar trava com "Incluir ART/RRT". Só linha NOVA ou que TROCOU de item dispara — orçamento antigo com
// equipamento e sem RRT continua salvando (mesma régua da API, OrcamentoItensObrigatorios).
//
// 25/10 (D-0104): ART/RRT deixaram de ser obrigatórias. A migration da API moveu as duas regras para
// orcamento_config.itens_sugeridos (mesmo formato): a tela ainda cria a linha ao escolher o equipamento, mas
// apagar não trava nada. A sugestão só entra quando o grupo ganha a PRIMEIRA linha que dispara a regra — quem
// apagou a ART/RRT não a vê voltar a cada equipamento novo.
import { linhaConfereObservacao } from './observacaoObrigatoriaPolicy';
import { nomeItemAviso } from './nomeItemAvisoPolicy';

function config(produto) {
    const valor = produto?.orcamentoConfig ?? produto?.orcamento_config ?? null;
    if (typeof valor === 'string') {
        try { return JSON.parse(valor); } catch (e) { return null; }
    }
    return valor && typeof valor === 'object' ? valor : null;
}

const id = (valor) => (valor === null || valor === undefined ? '' : String(valor));

function regrasDaChave(grupo, chave) {
    const regras = config(grupo?.produto)?.[chave];
    return (Array.isArray(regras) ? regras : [])
        .filter((r) => r && r.produto && ['itens', 'qualquer_item'].includes(r.quando));
}

export function regrasDoGrupo(grupo) {
    return regrasDaChave(grupo, 'itens_obrigatorios');
}

export function regrasSugeridasDoGrupo(grupo) {
    return regrasDaChave(grupo, 'itens_sugeridos');
}

export function linhaDispara(regra, produtoId) {
    const pid = id(produtoId);
    if (!pid || pid === id(regra.produto)) return false;
    if (regra.quando === 'itens') return (regra.itens || []).map(id).includes(pid);
    return !(regra.exceto || []).map(id).includes(pid);
}

// [{ regra, gatilhoIndex }] — regra cujo item obrigatório falta no grupo, disparada por linha nova/trocada.
export function faltantesNoGrupo(grupo) {
    const linhas = grupo?.subitem || [];
    const presentes = linhas.map((s) => id(s?.produto?.id));
    return regrasDoGrupo(grupo).reduce((acc, regra) => {
        if (presentes.includes(id(regra.produto))) return acc;
        const gatilhoIndex = linhas.findIndex((s) => linhaDispara(regra, s?.produto?.id) && linhaConfereObservacao(s));
        if (gatilhoIndex >= 0) acc.push({ regra, gatilhoIndex });
        return acc;
    }, []);
}

// [{ regra, gatilhoIndex }] — sugestão que falta no grupo, disparada pela linha que acabou de escolher o item,
// quando nenhuma outra linha do grupo dispara a mesma regra.
export function sugeridosAoEscolher(grupo, linhaIndex) {
    const linhas = grupo?.subitem || [];
    const escolhida = linhas[linhaIndex];
    if (!escolhida) return [];
    const presentes = linhas.map((s) => id(s?.produto?.id));
    return regrasSugeridasDoGrupo(grupo).reduce((acc, regra) => {
        if (presentes.includes(id(regra.produto)) || !linhaDispara(regra, escolhida?.produto?.id)) return acc;
        const outraDispara = linhas.some((s, i) => i !== linhaIndex && linhaDispara(regra, s?.produto?.id));
        if (!outraDispara) acc.push({ regra, gatilhoIndex: linhaIndex });
        return acc;
    }, []);
}

// Linhas que a tela cria: as obrigatórias que faltam e, ao escolher o item de uma linha, as sugeridas por ela.
export function linhasParaIncluir(grupo, linhaIndex = null) {
    const obrigatorias = faltantesNoGrupo(grupo);
    if (linhaIndex === null || linhaIndex === undefined) return obrigatorias;
    const jaIncluidas = obrigatorias.map((f) => id(f.regra.produto));
    return obrigatorias.concat(sugeridosAoEscolher(grupo, linhaIndex).filter((f) => !jaIncluidas.includes(id(f.regra.produto))));
}

// Pendências no formato do alerta do rodapé, com a ação "Incluir ART/RRT".
export function listarPendenciasItensObrigatorios(orcamento = {}) {
    const erros = [];
    (orcamento?.orcamentoitem || []).forEach((grupo, grupoIndex) => {
        faltantesNoGrupo(grupo).forEach(({ regra, gatilhoIndex }) => {
            const gatilho = nomeItemAviso(grupo.subitem[gatilhoIndex]?.produto) || regra.motivo || 'o item';
            erros.push({
                tipo: 'item_obrigatorio',
                mensagem: `${regra.nome} é obrigatória com ${gatilho} (${grupo?.produto?.nome || 'grupo'}).`,
                alvo: { grupoIndex, linhaIndex: gatilhoIndex, etapa: null, campo: null },
                acao: { rotulo: `Incluir ${regra.nome}`, grupoIndex },
            });
        });
    });
    return erros;
}

// Linha obrigatória criada a partir da linha que disparou: mesmo fornecedor/pagador, ligada a ela
// (linhaObrigatoriaDe) para herdar o fornecedor escolhido depois.
export function completarLinhaObrigatoria(linha, gatilho) {
    const nova = { ...linha, linhaObrigatoriaDe: gatilho?.chaveunica ?? null };
    if (gatilho?.fornecedor?.id) nova.fornecedor = { ...gatilho.fornecedor };
    if (gatilho?.fornecedorpagamento) nova.fornecedorpagamento = { ...gatilho.fornecedorpagamento };
    return nova;
}
